import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import { useSnackbar } from "notistack";

import DialogModal from "../../../../../../atoms/dialog"
import { APIRoutes } from "../../../../../../constants";
import { useFetch } from "../../../../../../utils/hooks/useFetch";
import { ExpertDetails } from "./types";
import { CLIENT_IMPORTANT_NOTES, getExpertData } from "./helper";
import { useGetParams } from "../../../../../../utils/hooks/useGetParams";
import CustomBtnFilled from "../../../../../../atoms/form-molecules/CustomBtnFilled";
import { ElementRef, useEffect, useRef, useState } from "react";
import RichTextDisplayer from "../../../../../../atoms/rich-text-editor/RichTextDisplayer";
import { copyHtml } from "../../../../../../utils/copyHtml";
import { getPeMappingSharedUrlWithoutToken } from "../../../../../project-detail/mapping/helper";
import { useProjectDetailsContext } from "../../../../../../atoms/project-details/projectContext";
import { MetaType } from "../../../type";



type Props = {
    isOpen: boolean;
    handleClose: () => void;
    expert_id: number | null;
    relevant_company: string | null;
    relevant_designation: string | null;
    snippet: string | null;
    charges: string | null;
    meta: MetaType;
    forBulk?: Boolean;
    isBulkFirst?: boolean;
    componentHtml?: (html: any) => void;
    client_id?:  number
}

const EmailFormatDialog = ({ isOpen, handleClose, expert_id, relevant_company, relevant_designation, snippet, charges, meta, forBulk, isBulkFirst, componentHtml, client_id }: Props) => {
    const { data } = useFetch<ExpertDetails[]>(`${APIRoutes.getExpert}?id=${expert_id}&embed=YES`, {
        variables: [expert_id]
    })

    const [htmlGenerated, setHtmlGenerated] = useState(false);
    const project_id = useGetParams("project_id");
    const project_status = useGetParams("project_status");
    const importantNotes = client_id ? CLIENT_IMPORTANT_NOTES[client_id] : null;
    const { defaultValues } = useProjectDetailsContext();

    const expertData = data ? getExpertData(data[0], relevant_company, relevant_designation, charges) : null;

    const componentRef = useRef<ElementRef<"div">>(null);

    const { enqueueSnackbar } = useSnackbar();

    const handleCopyToClipboard = async () => {
        await copyHtml(componentRef, enqueueSnackbar);
    };

    useEffect(() => {
        (async () => {
            if (expertData && componentRef.current && componentHtml && !htmlGenerated) {
                setHtmlGenerated(true);
                const html = await copyHtml(componentRef, enqueueSnackbar, true);
                componentHtml(html);
            }
        })();
    }, [expertData, componentHtml]);

  const Note = () => {
    return (
      <>
        <span style={{
          fontStyle: "italic",
          fontSize: "12px",
          background: "#fff9e5",
          color: "#444",
          padding: "4px 8px",
          marginLeft: "8px",
          borderRadius: "6px"
        }}>
          Click on the link to access list of experts shared on this case
        </span>
      </>
    )
  }

    return (
        <>
            {forBulk ?
                <div>
                    {!expertData ? <CircularProgress sx={{ color: "#EC9324", mt: "10px" }} /> :
                        <Stack mt={"10px"} sx={{
                            fontFamily: `"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI",
            "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans",
            "Helvetica Neue", sans-serif`,
                            "& p": {
                                fontSize: "14px"
                            }
                        }}
                            flexDirection={"column"}
                            ref={componentRef}
                        >
                            {isBulkFirst &&
                              <>
                                <p style={{ color: "#EC9324", marginBottom: "10px" }}>
                                    <strong>
                                        <u>
                                            <a
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                href={getPeMappingSharedUrlWithoutToken(project_id, client_id, String(expert_id))}
                                            >
                                                Presentation Link
                                            </a>
                                        </u>
                                    </strong>
                                    <Note/>
                                </p>
                              </>
                            }
                            <p style={{ color: "#EC9324", fontWeight: "600", marginBottom: "10px" }}>
                                <u>
                                    <a
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        href={getPeMappingSharedUrlWithoutToken(project_id, client_id, String(expert_id))}
                                    >{expertData.name}
                                    </a>
                                </u>
                            </p>
                            <Box sx={{ marginBottom: "10px" }}>
                                {relevant_designation && relevant_company && expertData.rel_company_date &&
                                    <p>
                                        <span style={{ fontWeight: "600" }}>Relevant Designation & Company: </span>{relevant_designation} at {relevant_company} {expertData.rel_company_date}
                                    </p>
                                }

                                {
                                    expertData.curr_company_full &&
                                    <p>
                                        <span style={{ fontWeight: "600" }}>Current Designation & Company: </span>{expertData.curr_company_full}
                                    </p>
                                }

                                <p><span style={{ fontWeight: "600" }}>Geography: </span>{expertData.geography}</p>
                            </Box>

                            <Box style={{ marginBottom: "10px" }}>
                                {snippet && <RichTextDisplayer text={snippet} />}
                            </Box>
                            <Box style={{ marginBottom: "10px" }}>
                                {expertData.work_history.length ?
                                    <>
                                        <p style={{ fontWeight: "600" }}>Work History:</p>
                                        <ul style={{ listStyleType: 'disc', marginLeft: "24px" }}>
                                            {expertData.work_history.map(wx => <li key={wx}><p>{wx}</p></li>)}
                                        </ul>
                                    </> : ""
                                }
                            </Box>
                            {expertData.charges && <p><span style={{ fontWeight: "600" }}>Charges: </span>{expertData.charges} <span style={{ fontWeight: "500" }}>{expertData.premium_expert}</span></p>}
                            {!!meta.time_multiplier &&
                                <p>
                                    <span style={{ fontWeight: "600" }}>Multiplier: </span> {meta.time_multiplier}
                                </p>
                            }
                            
                            {meta.govt_employee &&
                                <p>
                                    <span style={{fontWeight: "600"}}>{meta.govt_employee.value === "Non-US" ? "" : meta.govt_employee.value} Government Employee {meta.govt_employee.location ? `( ${meta.govt_employee.location.label} )`: ""}</span>
                                </p>
                            }
                        </Stack>
                    }
                </div> :
                <DialogModal
                    isOpen={isOpen}
                    handleClose={handleClose}
                    title={"Expert Details"}
                    titleSx={{ paddingTop: "0" }}
                    TitleEl={
                        <CustomBtnFilled
                            label="Copy to clipboard"
                            variant="outlined"
                            onClick={handleCopyToClipboard}
                        />
                    }
                >
                    {!expertData ? <CircularProgress sx={{ color: "#EC9324", mt: "10px" }} /> :
                        <Stack mt={"10px"} sx={{
                            fontFamily: `"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI",
            "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans",
            "Helvetica Neue", sans-serif`,
                            "& p": {
                                fontSize: "14px"
                            }
                        }}
                            flexDirection={"column"}
                            ref={componentRef}
                        >
                        <>
                            <p style={{ color: "#EC9324", marginBottom: "10px" }}>
                                <strong>
                                    <u>
                                        <a
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            href={getPeMappingSharedUrlWithoutToken(project_id, client_id, String(expert_id))}
                                        >
                                            Presentation Link
                                        </a>
                                    </u>
                                </strong>
                                <Note />
                            </p>
                          </>
                            <p style={{ color: "#EC9324", fontWeight: "600", marginBottom: "10px" }}>
                                <u>
                                    <a
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        href={getPeMappingSharedUrlWithoutToken(project_id, client_id, String(expert_id))}
                                    >{expertData.name}
                                    </a>
                                </u>
                            </p>
                            <Box sx={{ marginBottom: "10px" }}>
                                {relevant_designation + " at " + relevant_company + " " + expertData.rel_company_date === expertData.curr_company_full ? (
                                    <p>
                                        <span style={{ fontWeight: "600" }}>Relevant/Current Designation & Company: </span>{relevant_designation} at {relevant_company} {expertData.rel_company_date}
                                    </p>
                                ) : (
                                    <>
                                        <p>
                                            <span style={{ fontWeight: "600" }}>Relevant Designation & Company: </span>{relevant_designation} at {relevant_company} {expertData.rel_company_date}
                                        </p>
                                        {expertData.curr_company_full && (
                                            <p>
                                                <span style={{ fontWeight: "600" }}>Current Designation & Company: </span>{expertData.curr_company_full}
                                            </p>
                                        )}
                                    </>
                                )}
                                <p><span style={{ fontWeight: "600" }}>Geography: </span>{expertData.geography}</p>
                            </Box>

                            <Box style={{ marginBottom: "10px" }}>
                                {snippet && <RichTextDisplayer text={snippet} />}
                            </Box>
                            <Box style={{ marginBottom: "10px" }}>
                                {expertData.work_history.length ?
                                    <>
                                        <p style={{ fontWeight: "600" }}>Work History:</p>
                                        <ul style={{ listStyleType: 'disc', marginLeft: "24px" }}>
                                            {expertData.work_history
                                               
                                                .map(wx => (
                                                    <li key={wx}><p>{wx}</p></li>
                                                ))}
                                        </ul>
                                    </> : ""
                                }
                            </Box>
                            {expertData.charges && <p><span style={{ fontWeight: "600" }}>Charges: </span>{expertData.charges} <span style={{ fontWeight: "500" }}>{expertData.premium_expert}</span></p>}
                            {!!meta.time_multiplier &&
                                <p>
                                    <span style={{ fontWeight: "600" }}>Multiplier: </span> {meta.time_multiplier}
                                </p>
                            }

                            {meta.govt_employee &&
                                <p>
                                    <span style={{fontWeight: "600"}}>{meta.govt_employee.value === "Non-US" ? "" : meta.govt_employee.value} Government Employee {meta.govt_employee.location ? `( ${meta.govt_employee.location.label} )`: ""}</span>
                                </p>
                            }
                            {
                              importantNotes ?
                                (
                                  <div
                                    style={{
                                      backgroundColor: "#f8f9fa",
                                      border: "1px solid #e9ecef",
                                      borderRadius: "8px",
                                      padding: "16px",
                                      marginTop: "16px",
                                      fontFamily: '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif'
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "14px",
                                        fontWeight: "600",
                                        color: "#495057",
                                        marginBottom: "8px"
                                      }}
                                    >
                                      Please Note:
                                    </div>
                                    {importantNotes.map((note, index) => (
                                      <div
                                        key={index}
                                        style={{
                                          fontSize: "13px",
                                          color: "#6c757d",
                                          marginBottom: index < importantNotes.length - 1 ? "4px" : "0",
                                          fontStyle: "italic"
                                        }}
                                      >
                                        - {note}
                                      </div>
                                    ))}
                                  </div>
                                )
                                : null
                            }
                        </Stack>
                    }
                  
                </DialogModal>}
        </>
    )
}

export default EmailFormatDialog